import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  chuyen_giao_chuyen_nhuong_don_van_bang,
  dang_ky_ban_quyen,
  dang_ky_gia_han,
  dang_ky_kieu_dang_cong_nghiep,
  dang_ky_nhan_hieu,
  dang_ky_sang_che_giai_phap_huu_ich,
  dang_ky_sua_doi_don_van_bang,
  dich_vu_khac,
  tra_cuu_thong_tin,
} from "../../danh-sach-dich-vu/data/form_data";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { mapFormData } from "../../../../utils/MappingFormData";
import Loading from "../../components/Loading";
import DynamicForm from "../../danh-sach-dich-vu/forms/DynamicForm";
import { listFileService } from "../../../../services/api/listFileService";
import BackNavigationBar from "../../components/BackNavigationBar";

const FORM_MAP = {
  "dang-ky-nhan-hieu": dang_ky_nhan_hieu,
  "dang-ky-kieu-dang-cong-nghiep": dang_ky_kieu_dang_cong_nghiep,
  "dang-ky-sang-che-giai-phap-huu-ich": dang_ky_sang_che_giai_phap_huu_ich,
  "dang-ky-gia-han": dang_ky_gia_han,
  "dang-ky-sua-doi-don-van-bang": dang_ky_sua_doi_don_van_bang,
  "chuyen-giao-chuyen-nhuong-don-van-bang":
    chuyen_giao_chuyen_nhuong_don_van_bang,
  "tra-cuu-thong-tin": tra_cuu_thong_tin,
  "dang-ky-ban-quyen": dang_ky_ban_quyen,
  "dich-vu-khac": dich_vu_khac,
};

const EditProfile = ({ isMobile }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token_client } = useContext(AuthContext);

  // loading chuẩn: bật ngay khi mount, có cờ hasLoadedOnce để phân biệt lần đầu
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [formData, setFormData] = useState([]);
  const [data, setData] = useState({});
  const [info, setInfo] = useState([]);
  const [represent, setRepresent] = useState(null);
  const [brand, setBrand] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [serviceName, setServiceName] = useState("");

  const statusCheckedRef = useRef(false);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort?.();
      } catch {}
    };
  }, []);

  const formatData = (res) => {
    const profile = res.profile || {};
    const registeredService = profile.registeredService || {};
    const serviceId = registeredService.serviceId || {};
    return {
      formName: serviceId.formName || "",
      profile_name: serviceId.serviceName || "",
      profile_image: profile.image?.url || "",
      isLookup: profile.isLookup || false,
      isRequire: profile.isRequire || false,
      isOriginal: profile.isOriginal || false,
      contentRequire: profile.contentRequire || "",
    };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // nếu service hỗ trợ AbortController, bạn có thể truyền signal dưới đây
      abortRef.current?.abort?.();
      abortRef.current = new AbortController();

      const res = await listFileService.get_by_id(
        id,
        token_client /*, { signal: abortRef.current.signal }*/
      );

      if (!mountedRef.current) return;

      const formName = res?.data?.profile?.serviceId?.formName || "";
      const mapped = FORM_MAP[formName] || null;

      if (!mapped) {
        toast.dismiss();
        toast.error("Không tồn tại form có tên như trên!!");
        setFormData([]);
      } else {
        setFormData(mapped);
      }

      setCategory(res.data.profile.serviceId.category.categoryName);
      setRepresent(res.data.profile?.represent ?? null);
      setBrand(res.data.profile?.brand);
      setData(formatData(res.data));
      setInfo(res.data.profile.info);
      setStatus(res.data.profile.status);
      setServiceName(res.data?.profile?.serviceId?.serviceName || "");
    } catch (error) {
      if (!mountedRef.current) return;
      toast.dismiss();
      toast.error(
        error?.res
          ? "Lỗi từ server: " + (error.res.data?.message || "Không xác định")
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng."
      );
      console.error("Lấy data Staff bị lỗi:", error);
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [id, token_client]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Map info -> initData khi cả hai đã sẵn sàng
  useEffect(() => {
    setData((prevData) => {
      if (!info || !formData || info.length === 0 || formData.length === 0) {
        console.warn("INFO hoặc FORM DATA rỗng, giữ nguyên data cũ");
        return prevData;
      }
      const newInfo = mapFormData(info, formData);
      return {
        ...prevData,
        info: newInfo,
      };
    });
  }, [info, formData]);

  // Không cho chỉnh sửa nếu đã qua "Chờ duyệt" (đưa vào effect để tránh toast trong render)
  useEffect(() => {
    if (!hasLoadedOnce || statusCheckedRef.current) return;
    if (status && status !== "Chờ duyệt") {
      statusCheckedRef.current = true;
      toast.dismiss();
      toast.error("Hồ sơ đã được duyệt, bạn không thể chỉnh sửa!");
      navigate(`/quan-ly-ho-so/ho-so/${id}`);
    }
  }, [status, hasLoadedOnce, navigate, id]);

  // Lần tải đầu tiên hiển thị Loading
  if (loading && !hasLoadedOnce) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  // Khi chưa map xong info -> form, vẫn hiển thị loading
  if (!data?.info) {
    return (
      <div
        className={`flex ${
          isMobile ? "h-screen w-screen" : "h-full"
        } items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.99 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="h-full w-full pb-20"
    >
      <BackNavigationBar
        ariaLabel="Quay lại"
        onBack={() => navigate(-1)}
        subtitle={category}
        title={serviceName || (Array.isArray(formData) && formData?.name) || ""}
      />

      <DynamicForm
        formData={formData}
        initData={data.info}
        data={data}
        id={id}
        token_client={token_client}
        isEdit={true}
        representData={represent || undefined}
        branData={brand}
        category={category}
        isMobile={isMobile}
        serviceName={serviceName}
      />
    </motion.div>
  );
};

export default EditProfile;
